import { createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../gql/api';
import { v4 as uuidv4 } from 'uuid';
import imageCompression from 'browser-image-compression';
import i18n from '../../i18n';
import { setCurrentWorkspace } from '../slices/currentWorkspace';
import { loadDefaultData } from './currentWorkspace';

export const createWorkspace = createAsyncThunk(
  'workspaces/createWorkspace',
  async (payload) => {
    const { workspaceName, mode, onError, onSuccess } = payload;

    try {
      if (workspaceName && workspaceName.trim() !== '') {
        if (mode && (mode === 'simple' || mode === 'advanced')) {
          const response = await api.workspace.createWorkspace({
            data: {
              name: workspaceName,
              mode,
            },
          });

          if (response?.data?.createWorkspace) {
            onSuccess(response.data.createWorkspace);
            return response.data.createWorkspace;
          }
          throw new Error('responseError');
        } else {
          throw new Error('workspaceModeError');
        }
      } else {
        throw new Error('workspaceNameError');
      }
    } catch (error) {
      if (
        error.message === 'workspaceNameError' ||
        error.message === 'workspaceModeError'
      ) {
        onError(error.message);
      } else {
        console.log(error);
        onError('generalError');
      }
    }
    return null;
  }
);

export const updateWorkspaceById = createAsyncThunk(
  'workspaces/updateWorkspaceById',
  async (payload) => {
    const { id, data } = payload;
    const workspaceUpdated = await api.workspace.updateWorkspaceById({
      id,
      data,
    });
    return workspaceUpdated.data.updateWorkspaceById;
  }
);

/**
 * Superadmin entry into a workspace they hold no user_role in.
 *
 * Fetches the workspace so it can be upserted into the workspaces entity store —
 * everything downstream (currentWorkspaceSelectors, MainLayout, Sidebar) resolves
 * workspace identity through that adapter, so the upsert is what makes the rest
 * of the app work unchanged.
 *
 * Authorization is NOT decided here: getAllWorkspaces requires workspace.view at
 * system scope, so a non-superadmin gets FORBIDDEN from the server.
 */
export const getWorkspaceByIdAsSuperAdmin = createAsyncThunk(
  'workspaces/getWorkspaceByIdAsSuperAdmin',
  async (workspaceId, { dispatch }) => {
    const { data } = await api.workspace.getWorkspaceById({ id: workspaceId });
    const workspace = data?.getAllWorkspaces?.workspaces?.[0];

    // A well-formed but nonexistent UUID comes back as an empty array. That has
    // to reject, otherwise the guard sits on a LoadingScreen forever. (A
    // malformed UUID makes Postgres throw, which rejects on its own.)
    if (!workspace) {
      throw new Error('workspace_not_found');
    }

    // AuthGuard also tests `_.isEmpty(currentWorkspace)`, so upserting the entity
    // is not enough on its own — the admin's persisted currentWorkspace is null
    // or points somewhere else. Set it here.
    dispatch(setCurrentWorkspace(workspaceId));

    // Deliberately not awaited: loadDefaultData unwraps four sub-thunks, and a
    // transient brands/models failure should not bounce the admin out of the
    // workspace. Mirrors the existing AuthGuard behaviour.
    dispatch(loadDefaultData(workspaceId));

    return {
      ...workspace,
      // WorkspaceCard treats a missing `role` as "pending invitation" and links
      // to /workspace-invitation?token=undefined. Sidebar renders it as a label.
      role: i18n.t('workspace.super_admin_role'),
    };
  },
  {
    condition: (workspaceId, { getState }) => {
      const { workspaces } = getState();
      // Already in the store (member, or fetched earlier this session).
      if (workspaces.entities[workspaceId]) return false;
      // In flight, or already failed — don't retry into a loop.
      const lookup = workspaces.superAdminLookup;
      if (
        lookup.id === workspaceId &&
        (lookup.status === 'loading' || lookup.status === 'failed')
      ) {
        return false;
      }
      return true;
    },
  }
);

export const updateWorkspaceLogo = createAsyncThunk(
  'workspaces/updateWorkspaceLogo',
  async (payload) => {
    const { file } = payload;
    const pictureName = `${uuidv4()}.webp`;

    const { data } = await api.upload.getUploadUrl({
      filename: pictureName,
    });

    const options = {
      maxWidthOrHeight: 200,
      useWebWorker: true,
      fileType: 'image/webp',
    };

    const compressedImage = await imageCompression(file, options);

    const requestOptions = {
      method: 'PUT',
      headers: {
        'Content-Type': compressedImage.type,
      },
      mode: 'cors',
      body: compressedImage,
    };

    const uploadToS3Response = await fetch(
      data?.getUploadSignedUrl?.url,
      requestOptions
    );

    if (uploadToS3Response.ok) {
      return pictureName;
    } else {
      throw new Error('Error uploading image');
    }
  }
);
